// ============================================================================
// Reporting & Deliverability — frontend module.
// Mirrors SignalHouse's own Analytics page (SMS, outbound + inbound) from
// GET /api/reports: OUTBOUND + INBOUND stat tiles, three delivery-rate bar charts
// (by date / by number / by carrier), and a server-paginated Delivery Rate Logs
// table with rows-per-page + Export CSV. Keeps the Account + Range selectors.
// Uses only the existing shared classes (card/pad, toolbar, stat-row/stat, table-
// wrap/h-table, h-input, btn/btn ghost, pill, empty, msg) — inline style="" for
// small tweaks and the CSS bars only.
// ============================================================================
import { api } from '../lib/api.js'
import { esc } from '../lib/view.js'
import { numberLabel, fmtPhone, etDT } from '../lib/format.js'
import { errorInfo } from '../lib/errorcodes.js'

const nf = (n) => Number(n || 0).toLocaleString('en-US')
// A tile value: number → localized, null/undefined → em dash.
const numOr = (v) => (v == null ? '—' : nf(v))
// A percentage tile value: number → "NN.N%", null → em dash.
const pctOr = (v) => (v == null ? '—' : (Math.round(Number(v) * 10) / 10) + '%')

export function renderReports(el) {
  el.innerHTML = `
    <div class="card pad">
      <div class="toolbar">
        <div class="grow">
          <div class="mod-h">Compliance &amp; Deliverability</div>
          <div class="mod-sub">SMS analytics — outbound &amp; inbound, timezone-aligned (America/New_York)</div>
        </div>
        <label class="mod-sub" style="margin:0" for="rp-account">Account</label>
        <select class="h-input" id="rp-account" style="max-width:170px">
          <option value="all">All accounts</option>
        </select>
        <label class="mod-sub" style="margin:0" for="rp-days">Range</label>
        <select class="h-input" id="rp-days" style="max-width:150px">
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="7" selected>Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="custom">Custom range…</option>
        </select>
        <span id="rp-custom" style="display:none;align-items:center;gap:6px">
          <input class="h-input" type="date" id="rp-from" style="max-width:150px" />
          <span class="mod-sub" style="margin:0">to</span>
          <input class="h-input" type="date" id="rp-to" style="max-width:150px" />
          <button class="btn ghost" id="rp-apply">Apply</button>
        </span>
      </div>
      <div id="rp-body"><div class="msg">Loading analytics…</div></div>
    </div>`

  const body = el.querySelector('#rp-body')
  const sel = el.querySelector('#rp-days')
  const acct = el.querySelector('#rp-account')
  const custom = el.querySelector('#rp-custom')
  const fromInp = el.querySelector('#rp-from')
  const toInp = el.querySelector('#rp-to')
  let acctPopulated = false

  // Log pagination state (server-side). Reset to page 0 on any account/range change.
  const logState = { page: 0, size: 10 }

  // ET calendar date (YYYY-MM-DD) — the analytics are ET-aligned, so Today means
  // the ET day regardless of the viewer's own timezone.
  const etDate = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)

  // Map the Range selection → API params (a days preset OR an explicit start/end).
  function rangeParams() {
    const v = sel.value
    if (v === 'today') { const t = etDate(new Date()); return { start: t, end: t } }
    if (v === 'yesterday') { const y = etDate(new Date(Date.now() - 864e5)); return { start: y, end: y } }
    if (v === 'custom') {
      if (fromInp.value && toInp.value) return { start: fromInp.value, end: toInp.value }
      return { days: '7' }
    }
    return { days: v }
  }

  const query = (over = {}) => {
    const p = new URLSearchParams({
      account: acct.value,
      logPage: String(over.page ?? logState.page),
      logSize: String(over.size ?? logState.size),
    })
    for (const [k, val] of Object.entries(rangeParams())) p.set(k, val)
    return '/api/reports?' + p.toString()
  }

  async function fullLoad() {
    logState.page = 0
    body.innerHTML = '<div class="msg">Loading analytics…</div>'
    try {
      const data = await api.get(query())
      if (!acctPopulated && Array.isArray(data.accounts)) {
        acct.innerHTML = '<option value="all">All accounts</option>' +
          data.accounts.map((a) => `<option value="${esc(a.id)}">${esc(a.name)}</option>`).join('')
        acct.value = data.account || 'all'
        acctPopulated = true
      }
      logState.size = Number(data.logSize) || 10
      body.innerHTML = renderBody(data)
      wireLog(el, logState, query, data)
    } catch (e) {
      body.innerHTML = '<div class="msg bad">Could not load the report: ' + esc(e.message) + '</div>'
    }
  }

  sel.addEventListener('change', () => {
    if (sel.value === 'custom') {
      custom.style.display = 'inline-flex'
      if (!toInp.value) { toInp.value = etDate(new Date()); fromInp.value = etDate(new Date(Date.now() - 6 * 864e5)) }
      // Wait for Apply — don't reload on an empty custom range.
    } else {
      custom.style.display = 'none'
      fullLoad()
    }
  })
  el.querySelector('#rp-apply').addEventListener('click', () => { if (fromInp.value && toInp.value) fullLoad() })
  acct.addEventListener('change', fullLoad)

  // Clickable tiles → smooth-scroll to the matching section (delegated so it
  // survives every re-render). Tiles carry data-jump="<section anchor id>".
  el.addEventListener('click', (e) => {
    const tileEl = e.target.closest('[data-jump]')
    if (!tileEl) return
    const target = el.querySelector('#' + tileEl.getAttribute('data-jump'))
    if (!target) return
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    const prev = target.style.background
    target.style.transition = 'background .25s'
    target.style.background = 'var(--pink-weak, rgba(253,77,150,.12))'
    setTimeout(() => { target.style.background = prev || '' }, 800)
  })
  el.addEventListener('keydown', (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.matches('[data-jump]')) { e.preventDefault(); e.target.click() }
  })

  fullLoad()
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------
function renderBody(data) {
  const providers = Array.isArray(data.providers) ? data.providers : []
  const byDay = Array.isArray(data.byDay) ? data.byDay : []
  const byNumber = Array.isArray(data.byNumber) ? data.byNumber : []
  const byCarrier = Array.isArray(data.byCarrier) ? data.byCarrier : []
  const out = data.outbound || {}
  const inb = data.inbound || {}

  const nothing = !out.sent && !out.delivered && !inb.received && !byDay.length && !(data.log || []).length
  if (nothing) {
    const why = data.note ? ' (' + esc(data.note) + ')' : ''
    return '<div class="msg bad">No analytics available for this range' + why + '.</div>'
  }

  return [
    section('Outbound', outboundTiles(out)),
    section('Inbound', inboundTiles(inb)),
    rateChart('Delivery Rate — by Date', byDay.map((d) => ({ label: d.day, rate: d.rate, sub: d.delivered + ' del' })), 'date', 'sec-bydate'),
    rateChart('Delivery Rate — by Number', byNumber.slice(0, 14).map((n) => ({ label: numberLabel(n.number), rate: n.rate, sub: nf(n.total) })), 'number'),
    rateChart('Delivery Rate — by Carrier', byCarrier.slice(0, 14).map((c) => ({ label: c.carrier, rate: c.rate, sub: nf(c.count) })), 'carrier'),
    failedByReason(data.errors, out),
    linkDomainsTable(data.linkDomains),
    spamMessagesTable(data.spamMessages, data.spamRedacted),
    providers.length > 1 ? providerTable(providers) : '',
    logSection(data),
    data.note ? '<div class="msg" style="color:var(--muted)">Note: ' + esc(data.note) + '</div>' : '',
  ].join('')
}

function section(title, inner) {
  return '<div class="mod-h" style="margin-top:10px">' + esc(title) + '</div>' + inner
}

function tile(k, v, d, color, jump) {
  const vStyle = color ? ' style="color:' + color + '"' : ''
  const j = jump ? ' role="button" tabindex="0" data-jump="' + esc(jump) + '" title="View details" style="cursor:pointer"' : ''
  const arrow = jump ? ' <span aria-hidden="true" style="color:var(--muted);font-weight:400">↗</span>' : ''
  return '<div class="stat"' + j + '><div class="k">' + esc(k) + arrow + '</div><div class="v"' + vStyle + '>' + esc(v) +
    '</div>' + (d ? '<div class="d">' + esc(d) + '</div>' : '') + '</div>'
}

const GOAL = 90 // deliverability target — 90%+

// Delivery-rate tile that scores against the 90% goal (green at/above, red under).
function goalTile(rate) {
  if (rate == null) return tile('Delivery Rate', '—', 'goal 90%', null, 'sec-bydate')
  const r = Math.round(Number(rate) * 10) / 10
  const ok = r >= GOAL
  const sub = ok ? '✓ at/above 90% goal' : (Math.round((GOAL - r) * 10) / 10) + ' pts under 90% goal'
  return tile('Delivery Rate', r + '%', sub, ok ? 'var(--ok)' : 'var(--bad)', 'sec-bydate')
}

function outboundTiles(o) {
  return '<div class="stat-row" style="margin-bottom:16px">' +
    goalTile(o.deliveryRate) +
    tile('Messages delivered', numOr(o.delivered)) +
    tile('Outbound messages', numOr(o.sent)) +
    tile('Avg messages / day', numOr(o.avgPerDay)) +
    tile('Outbound segments', numOr(o.segments)) +
    tile('Avg segments / day', numOr(o.avgSegPerDay)) +
    tile('Unique clicks', numOr(o.uniqueClicks), null, null, 'sec-links') +
    tile('Clickthrough rate', pctOr(o.clickthroughRate), null, null, 'sec-links') +
    tile('Failed', numOr(o.failed), 'incl. undelivered', null, 'sec-failed') +
    tile('Failure rate', pctOr(o.failureRate), null, null, 'sec-failed') +
    '</div>'
}

function inboundTiles(i) {
  return '<div class="stat-row" style="margin-bottom:16px">' +
    tile('Messages received', numOr(i.received)) +
    tile('Avg received / day', numOr(i.avgReceivedPerDay)) +
    tile('Segments received', numOr(i.segments)) +
    tile('Avg segments / day', numOr(i.avgSegPerDay)) +
    tile('Non opt-out response rate', pctOr(i.nonOptOutResponseRate)) +
    tile('Total opt outs', numOr(i.optOuts)) +
    tile('Opt-out rate', pctOr(i.optOutRate)) +
    '</div>'
}

// ---------------------------------------------------------------------------
// Delivery-rate bar charts — a row of divs, height ∝ rate (0–100%), var(--turq).
// ---------------------------------------------------------------------------
function rateChart(title, items, kind, anchorId) {
  if (!items.length) return ''
  const H = 96 // px chart height
  const bars = items.map((it) => {
    const rate = Math.max(0, Math.min(100, Number(it.rate || 0)))
    const h = Math.max(Math.round((rate / 100) * H), 2)
    const under = rate < GOAL // misses the 90% goal → red
    const title2 = esc(it.label) + ' — ' + rate + '%' + (it.sub ? ' · ' + esc(it.sub) : '') + (under ? ' · under 90% goal' : '')
    const lab = truncate(String(it.label), kind === 'date' ? 6 : 12)
    return '<div title="' + title2 + '" style="flex:1 1 24px;min-width:24px;max-width:64px;display:flex;flex-direction:column;align-items:center;gap:4px">' +
      '<div style="font-size:10px;color:' + (under ? 'var(--bad)' : 'var(--muted)') + ';font-variant-numeric:tabular-nums">' + rate + '%</div>' +
      '<div style="display:flex;align-items:flex-end;height:' + H + 'px;width:100%">' +
        '<div style="width:100%;height:' + h + 'px;background:' + (under ? 'var(--bad)' : 'var(--turq)') + ';border-radius:3px 3px 0 0"></div>' +
      '</div>' +
      '<div style="font-size:10.5px;color:var(--text-2);text-align:center;line-height:1.2;word-break:break-word">' + esc(lab) + '</div>' +
    '</div>'
  }).join('')
  // 90% goal reference line across the plot (dashed), positioned from the top.
  const goalTop = Math.round((1 - GOAL / 100) * H) + 18 // +label row above the bars
  const goalLine = '<div style="position:absolute;left:12px;right:12px;top:' + goalTop + 'px;border-top:1px dashed var(--ok);opacity:.7"></div>' +
    '<div style="position:absolute;right:14px;top:' + (goalTop - 14) + 'px;font-size:10px;color:var(--ok)">90% goal</div>'
  return '<div class="mod-h" style="margin-top:6px"' + (anchorId ? ' id="' + anchorId + '"' : '') + '>' + esc(title) + '</div>' +
    '<div class="table-wrap" style="position:relative;margin-bottom:18px;padding:12px 14px">' + goalLine +
    '<div style="display:flex;align-items:flex-end;gap:8px;min-width:min-content">' + bars + '</div>' +
    '</div>'
}

function truncate(s, n) { return s.length > n ? s.slice(0, n - 1) + '…' : s }

// ---------------------------------------------------------------------------
// Providers table (preserved — shown only when more than one account is in scope).
// ---------------------------------------------------------------------------
function providerTable(providers) {
  const rows = providers.map((p) => {
    const att = Number(p.delivered || 0) + Number(p.failed || 0)
    const r = att > 0 ? Math.round((Number(p.delivered || 0) / att) * 100) : 0
    return '<tr>' +
      '<td>' + esc(p.name) + '</td>' +
      '<td class="num">' + nf(p.sent) + '</td>' +
      '<td class="num">' + nf(p.delivered) + '</td>' +
      '<td class="num">' + nf(p.failed) + '</td>' +
      '<td class="num">' + nf(p.optOuts) + '</td>' +
      '<td class="num">' + r + '%</td>' +
      '</tr>'
  }).join('')
  return '<div class="mod-h" style="margin-top:6px">By account</div>' +
    '<div class="table-wrap" style="margin-bottom:18px"><table class="h-table"><thead><tr>' +
    '<th>Account</th><th class="num">Sent</th><th class="num">Delivered</th><th class="num">Failed</th><th class="num">Opt-outs</th><th class="num">Rate</th>' +
    '</tr></thead><tbody>' + rows + '</tbody></table></div>'
}

// ---------------------------------------------------------------------------
// Failed — by reason. Turns the raw error-code counts into a compliance-readable
// breakdown: every failure reason, its category, count, and share of all failures.
// Spam-flagged reasons are highlighted (they damage sender reputation + trust
// score). Rate is % of FAILED (matches the provider's own error-rate view).
// ---------------------------------------------------------------------------
function failedByReason(errors, out) {
  const list = Array.isArray(errors) ? errors.filter((e) => e && e.code != null) : []
  if (!list.length) return ''
  const totalFailed = list.reduce((n, e) => n + Number(e.count || 0), 0) || 1
  const enriched = list
    .map((e) => ({ ...errorInfo(e.code), count: Number(e.count || 0) }))
    .sort((a, b) => b.count - a.count)
  const spamCount = enriched.filter((e) => e.spam).reduce((n, e) => n + e.count, 0)
  const spamPct = Math.round((spamCount / totalFailed) * 1000) / 10

  const rows = enriched.map((e) => {
    const pct = Math.round((e.count / totalFailed) * 1000) / 10
    const hl = e.spam ? ' style="background:rgba(253,77,150,.06)"' : ''
    const catPill = '<span class="pill' + (e.spam ? ' mk' : '') + '">' + esc(e.catLabel) + '</span>'
    return '<tr' + hl + '>' +
      '<td style="font-variant-numeric:tabular-nums;white-space:nowrap">' + esc(e.code) + '</td>' +
      '<td><b>' + esc(e.label) + '</b><div class="mod-sub" style="margin:2px 0 0;font-size:12px">' + esc(e.desc) + '</div></td>' +
      '<td>' + catPill + '</td>' +
      '<td class="num">' + nf(e.count) + '</td>' +
      '<td class="num">' + pct + '%</td>' +
      '</tr>'
  }).join('')

  const spamNote = spamCount > 0
    ? ' <span class="pill mk" style="margin-left:8px">' + nf(spamCount) + ' spam-flagged · ' + spamPct + '% of failures</span>'
    : ''
  return '<div class="mod-h" style="margin-top:6px" id="sec-failed">Failed — by reason' + spamNote + '</div>' +
    '<div class="mod-sub" style="margin:0 0 8px">Every failed message grouped by why it failed. Spam-flagged reasons hurt deliverability and your carrier trust score — prioritize those.</div>' +
    '<div class="table-wrap" style="margin-bottom:18px"><table class="h-table"><thead><tr>' +
      '<th>Code</th><th>Reason</th><th>Category</th><th class="num">Count</th><th class="num">% of failed</th>' +
    '</tr></thead><tbody>' + rows + '</tbody></table></div>'
}

// ---------------------------------------------------------------------------
// Link domains — deliverability by the domain in the message. Carriers flag on
// the LINK, so a domain with a high fail rate is the thing to fix/rotate. Rows
// over 25% fail are drawn red.
// ---------------------------------------------------------------------------
function linkDomainsTable(domains) {
  const list = Array.isArray(domains) ? domains.filter((d) => d && d.domain) : []
  if (!list.length) return ''
  const rows = list.map((d) => {
    const prov = d.provider === 'twilio' ? 'Twilio' : 'SignalHouse'
    const bad = d.failRate >= 25
    const rateStyle = bad ? ' style="color:var(--bad);font-weight:700"' : (d.failRate >= 10 ? ' style="color:var(--warn)"' : '')
    return '<tr>' +
      '<td style="font-variant-numeric:tabular-nums">' + esc(d.domain) + '</td>' +
      '<td>' + esc(prov) + '</td>' +
      '<td class="num">' + nf(d.total) + '</td>' +
      '<td class="num">' + nf(d.failed) + '</td>' +
      '<td class="num"' + rateStyle + '>' + d.failRate + '%</td>' +
      '</tr>'
  }).join('')
  return '<div class="mod-h" style="margin-top:6px" id="sec-links">Link domains — deliverability</div>' +
    '<div class="mod-sub" style="margin:0 0 8px">Every link domain that appears in your messages, and how much of it fails. Carriers flag on the link — a high-fail domain (red) is a shortener/domain to rotate or retire.</div>' +
    '<div class="table-wrap" style="margin-bottom:18px"><table class="h-table"><thead><tr>' +
      '<th>Link domain</th><th>Provider</th><th class="num">Sent</th><th class="num">Failed</th><th class="num">Fail rate</th>' +
    '</tr></thead><tbody>' + rows + '</tbody></table></div>'
}

// ---------------------------------------------------------------------------
// Spam-flagged messages — the ACTUAL content carriers blocked, grouped by
// template (most-blocked first) so you can read the copy + link and decide what
// to change. Recipient numbers are masked server-side; body is the point.
// ---------------------------------------------------------------------------
function spamMessagesTable(msgs, redacted) {
  if (redacted) {
    return '<div class="mod-h" style="margin-top:6px">Spam-flagged messages</div>' +
      '<div class="msg" style="color:var(--muted)">Message content is hidden for your role. Ask an admin for agent access to review flagged copy.</div>'
  }
  const list = Array.isArray(msgs) ? msgs : []
  if (!list.length) return ''
  const totalBlocked = list.reduce((n, m) => n + Number(m.count || 0), 0)
  const rows = list.map((m) => {
    const prov = m.provider === 'twilio' ? 'Twilio' : 'SignalHouse'
    const froms = (m.froms || []).map((f) => numberLabel(f) || fmtPhone(f)).join(', ')
    return '<tr>' +
      '<td style="white-space:nowrap">' + esc(prov) + '</td>' +
      '<td style="font-variant-numeric:tabular-nums;white-space:nowrap">' + esc(m.code) + '</td>' +
      '<td class="num" style="color:var(--pink);font-weight:700">' + nf(m.count) + '</td>' +
      '<td style="max-width:220px;font-size:12px;color:var(--text-2)">' + esc(froms || '—') + '</td>' +
      '<td style="max-width:560px"><div style="white-space:pre-wrap;word-break:break-word;font-size:12.5px' + (m.body ? '' : ';color:var(--muted);font-style:italic') + '">' + esc(m.body || 'Content composed in Customer.io (not stored here) — see the link-domain table above') + '</div></td>' +
      '</tr>'
  }).join('')
  return '<div class="mod-h" style="margin-top:6px" id="sec-spam">Spam-flagged messages — the actual content' +
      ' <span class="pill mk" style="margin-left:8px">' + nf(totalBlocked) + ' blocked · ' + list.length + ' distinct</span></div>' +
    '<div class="mod-sub" style="margin:0 0 8px">The real message bodies carriers blocked, grouped by template (most-blocked first). Read the copy &amp; link to decide what to change — recipient numbers are masked.</div>' +
    '<div class="table-wrap" style="margin-bottom:18px"><table class="h-table"><thead><tr>' +
      '<th>Provider</th><th>Code</th><th class="num">Blocked</th><th>From</th><th>Message</th>' +
    '</tr></thead><tbody>' + rows + '</tbody></table></div>'
}

// ---------------------------------------------------------------------------
// Delivery Rate Logs — server-paginated table + rows-per-page + Export CSV.
// ---------------------------------------------------------------------------
function logSection(data) {
  return '<div class="mod-h" style="margin-top:6px">Delivery Rate Logs</div>' +
    '<div class="toolbar" style="margin-bottom:8px">' +
      '<span class="grow"></span>' +
      '<select class="h-input" id="rp-log-size" style="width:auto">' +
        sizeOpt(10, data.logSize) + sizeOpt(25, data.logSize) + sizeOpt(50, data.logSize) +
      '</select>' +
      '<button class="btn ghost" id="rp-log-export">Export CSV</button>' +
    '</div>' +
    '<div class="table-wrap"><table class="h-table"><thead><tr>' +
      '<th>Date/Time</th><th>Direction</th><th>Type</th><th>From</th><th>To</th>' +
      '<th>Carrier</th><th>Body</th><th class="num">Short URLs</th><th class="num">Clickthrough</th><th class="num">Segments</th>' +
    '</tr></thead><tbody id="rp-log-rows">' + logRows(data.log) + '</tbody></table></div>' +
    '<div class="toolbar" style="margin-top:12px;margin-bottom:0">' +
      '<span class="mod-sub" id="rp-log-count" style="margin:0">' + logCount(data) + '</span>' +
      '<span class="grow"></span>' +
      '<button class="btn ghost" id="rp-log-prev">‹ Prev</button>' +
      '<button class="btn ghost" id="rp-log-next">Next ›</button>' +
    '</div>'
}

function sizeOpt(n, cur) {
  return '<option value="' + n + '"' + (Number(cur) === n ? ' selected' : '') + '>' + n + ' / page</option>'
}

function dirPill(d) {
  const s = String(d || '').toLowerCase()
  if (s === 'inbound') return '<span class="pill an">Inbound</span>'
  if (s === 'outbound') return '<span class="pill">Outbound</span>'
  return '<span class="pill">' + esc(d || '—') + '</span>'
}

function logRows(log) {
  const rows = Array.isArray(log) ? log : []
  if (!rows.length) return '<tr><td colspan="10" class="empty">No messages in this range.</td></tr>'
  return rows.map((m) => '<tr>' +
    '<td class="num">' + (m.createdAt ? esc(etDT(m.createdAt)) : '—') + '</td>' +
    '<td>' + dirPill(m.direction) + '</td>' +
    '<td>' + esc(m.type || '—') + '</td>' +
    '<td class="num">' + esc(m.from ? fmtPhone(m.from) : '—') + '</td>' +
    '<td class="num">' + esc(m.to ? fmtPhone(m.to) : '—') + '</td>' +
    '<td>' + esc(m.carrier || '—') + '</td>' +
    '<td title="' + esc(m.body || '') + '">' + esc(truncate(String(m.body || ''), 48)) + '</td>' +
    '<td class="num">' + (m.shortUrls == null ? '—' : nf(m.shortUrls)) + '</td>' +
    '<td class="num">' + (m.clickthrough == null ? '—' : nf(m.clickthrough)) + '</td>' +
    '<td class="num">' + (m.segments == null ? '—' : nf(m.segments)) + '</td>' +
    '</tr>').join('')
}

function logCount(data) {
  const total = Number(data.logTotal || 0)
  const size = Number(data.logSize || 10)
  const page = Number(data.logPage || 0)
  const shown = Array.isArray(data.log) ? data.log.length : 0
  const from = total === 0 || shown === 0 ? 0 : page * size + 1
  const to = page * size + shown
  return 'Showing ' + from + '–' + to + ' of ' + nf(total)
}

// Wire the log controls once per full render. Page/size changes re-fetch and swap
// ONLY the log rows + footer, so the tiles and charts above stay put.
function wireLog(el, logState, query, initial) {
  const $ = (id) => el.querySelector('#' + id)
  const rows = $('rp-log-rows'), count = $('rp-log-count')
  const prev = $('rp-log-prev'), next = $('rp-log-next')
  const sizeSel = $('rp-log-size'), exportBtn = $('rp-log-export')
  if (!rows) return

  function setPager(total, size) {
    const pages = Math.max(Math.ceil(Number(total || 0) / Number(size || 10)), 1)
    prev.disabled = logState.page <= 0
    next.disabled = logState.page >= pages - 1
    prev.style.opacity = prev.disabled ? '.5' : '1'
    next.style.opacity = next.disabled ? '.5' : '1'
  }

  async function reload() {
    rows.innerHTML = '<tr><td colspan="10" class="empty">Loading…</td></tr>'
    try {
      const data = await api.get(query())
      rows.innerHTML = logRows(data.log)
      count.textContent = logCount(data)
      setPager(data.logTotal, data.logSize)
    } catch (e) {
      rows.innerHTML = '<tr><td colspan="10" class="empty">Could not load log: ' + esc(e.message) + '</td></tr>'
    }
  }

  sizeSel.addEventListener('change', () => { logState.size = parseInt(sizeSel.value, 10) || 10; logState.page = 0; reload() })
  prev.addEventListener('click', () => { if (logState.page > 0) { logState.page--; reload() } })
  next.addEventListener('click', () => { logState.page++; reload() })

  exportBtn.addEventListener('click', async () => {
    const label = exportBtn.textContent; exportBtn.textContent = 'Exporting…'; exportBtn.disabled = true
    try {
      const data = await api.get(query({ page: 0, size: 100000 }))
      const csv = toCsv(data.log || [])
      const blob = new Blob([csv], { type: 'text/csv' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = 'hive-delivery-log-' + new Date().toISOString().slice(0, 10) + '.csv'
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(a.href)
    } catch (e) { alert('Export failed: ' + e.message) }
    finally { exportBtn.textContent = label; exportBtn.disabled = false }
  })

  // Set initial pager button state from the already-rendered first page (no refetch).
  if (initial) setPager(initial.logTotal, initial.logSize)
}

const CSV_COLS = ['createdAt', 'direction', 'type', 'from', 'to', 'carrier', 'body', 'shortUrls', 'clickthrough', 'segments']
function toCsv(list) {
  const cell = (m, col) => {
    let v = m[col]
    if (v == null) v = ''
    v = String(v)
    if (/^[=+\-@\t\r]/.test(v)) v = "'" + v; return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v
  }
  return [CSV_COLS.join(','), ...list.map((m) => CSV_COLS.map((c) => cell(m, c)).join(','))].join('\n')
}
